"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Search, X, ArrowRight, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductSortDropdown, ProductsFilter, ProductPrice, EcommerceProduct } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
type Language = "en" | "zh" | "fr" | "es" | "de";
function useWvcLanguage(): Language {
  const [lang, setLang] = useState<Language>("en");
  useEffect(() => {
    const updateLang = () => {
      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get("lang");
        if (langParam && ["en", "zh", "fr", "es", "de"].includes(langParam)) {
          setLang(langParam as Language);
          return;
        }
        const docLang = document.documentElement.lang?.slice(0, 2);
        if (docLang && ["en", "zh", "fr", "es", "de"].includes(docLang)) {
          setLang(docLang as Language);
          return;
        }
      }
    };
    updateLang();
    window.addEventListener("popstate", updateLang);
    return () => window.removeEventListener("popstate", updateLang);
  }, []);
  return lang;
}
const translations = {
  en: {
    catalogueSearch: "Catalogue Search",
    findSignature: "Find your signature piece",
    searchDescription: "Search by silhouette, natural fabric, occasion, or seasonal capsule.",
    searchPlaceholder: "Search by keyword, e.g. Silk Dress, Linen Blazer...",
    popular: "Popular:",
    search: "Search",
    addBag: "Add to Bag",
    sale: "Sale",
    noResults: "No styles found matching your search",
    noMatches: "We couldn't find matches for",
    exploreEdits: "Explore our signature edits or search by fabric and silhouette.",
    refineTerms: "Refine your terms or discover pieces from our latest wardrobe curation.",
    suggestedTerms: "Suggested terms",
    curatedCategories: "Curated categories",
    trendingThisSeason: "Trending this season",
    viewFullCollection: "View Full Collection",
    loadMore: "Load More Styles",
    loading: "Loading...",
    allCollections: "All garment collections",
    searchResults: "Search results for",
    refineSearch: "Refine Search",
    filterCriteria: "Filter Criteria",
    close: "Close"
  },
  zh: {
    catalogueSearch: "目录搜索",
    findSignature: "找到您的签名件",
    searchDescription: "按剪裁、天然织物、场合或季节胶囊搜索。",
    searchPlaceholder: "按关键词搜索，例如 Silk Dress, Linen Blazer...",
    popular: "流行:",
    search: "搜索",
    addBag: "加入购物袋",
    sale: "销售",
    noResults: "未找到与您的搜索匹配的样式",
    noMatches: "我们找不到匹配的",
    exploreEdits: "探索我们的签名编辑或按织物和剪裁搜索。",
    refineTerms: "完善您的条款或从我们最新的衣柜策划中发现件。",
    suggestedTerms: "建议的条款",
    curatedCategories: "策划类别",
    trendingThisSeason: "本季趋势",
    viewFullCollection: "查看完整系列",
    loadMore: "加载更多样式",
    loading: "加载中...",
    allCollections: "所有服装系列",
    searchResults: "搜索结果为",
    refineSearch: "完善搜索",
    filterCriteria: "过滤条件",
    close: "关闭"
  },
  fr: {
    catalogueSearch: "Recherche de Catalogue",
    findSignature: "Trouvez votre pièce signature",
    searchDescription: "Recherchez par silhouette, tissu naturel, occasion ou capsule saisonnière.",
    searchPlaceholder: "Recherchez par mot-clé, par exemple Robe en Soie, Veste en Lin...",
    popular: "Populaire:",
    search: "Rechercher",
    addBag: "Ajouter au Panier",
    sale: "Solde",
    noResults: "Aucun style trouvé correspondant à votre recherche",
    noMatches: "Nous n'avons pas trouvé de correspondance pour",
    exploreEdits: "Explorez nos édits de signature ou recherchez par tissu et silhouette.",
    refineTerms: "Affinez vos conditions ou découvrez des pièces de notre dernière sélection de garde-robe.",
    suggestedTerms: "Termes Suggérés",
    curatedCategories: "Catégories Curées",
    trendingThisSeason: "Tendances Cette Saison",
    viewFullCollection: "Afficher la Collection Complète",
    loadMore: "Charger Plus de Styles",
    loading: "Chargement...",
    allCollections: "Toutes les Collections de Vêtements",
    searchResults: "Résultats de Recherche pour",
    refineSearch: "Affiner la Recherche",
    filterCriteria: "Critères de Filtre",
    close: "Fermer"
  },
  es: {
    catalogueSearch: "Búsqueda de Catálogo",
    findSignature: "Encuentra tu pieza de firma",
    searchDescription: "Busca por silueta, tela natural, ocasión o cápsula estacional.",
    searchPlaceholder: "Busca por palabra clave, p. ej. Vestido de Seda, Chaqueta de Lino...",
    popular: "Popular:",
    search: "Buscar",
    addBag: "Agregar a Carrito",
    sale: "Venta",
    noResults: "No se encontraron estilos que coincidan con tu búsqueda",
    noMatches: "No pudimos encontrar coincidencias para",
    exploreEdits: "Explora nuestras ediciones de firma o busca por tela y silueta.",
    refineTerms: "Refina tus términos o descubre piezas de nuestra última curaduría de guardarropa.",
    suggestedTerms: "Términos Sugeridos",
    curatedCategories: "Categorías Curadas",
    trendingThisSeason: "Tendencia Esta Temporada",
    viewFullCollection: "Ver Colección Completa",
    loadMore: "Cargar Más Estilos",
    loading: "Cargando...",
    allCollections: "Todas las Colecciones de Prendas",
    searchResults: "Resultados de Búsqueda para",
    refineSearch: "Refinar Búsqueda",
    filterCriteria: "Criterios de Filtro",
    close: "Cerrar"
  },
  de: {
    catalogueSearch: "Katalogssuche",
    findSignature: "Finden Sie Ihr Signaturteil",
    searchDescription: "Suchen Sie nach Silhouette, Naturfaser, Anlass oder saisonaler Kapsel.",
    searchPlaceholder: "Suchen Sie nach Schlüsselwort, z. B. Seidenkleid, Leinenblaser...",
    popular: "Beliebt:",
    search: "Suchen",
    addBag: "Zum Beutel hinzufügen",
    sale: "Verkauf",
    noResults: "Keine Stile gefunden, die Ihrer Suche entsprechen",
    noMatches: "Wir konnten keine Übereinstimmungen finden für",
    exploreEdits: "Erkunden Sie unsere Signature-Bearbeitungen oder suchen Sie nach Stoff und Silhouette.",
    refineTerms: "Verfeinern Sie Ihre Bedingungen oder entdecken Sie Teile aus unserer neuesten Garderobenkuratur.",
    suggestedTerms: "Vorgeschlagene Begriffe",
    curatedCategories: "Kuratierte Kategorien",
    trendingThisSeason: "Trend diese Saison",
    viewFullCollection: "Vollständige Kollektion Anzeigen",
    loadMore: "Weitere Stile Laden",
    loading: "Wird geladen...",
    allCollections: "Alle Kleidungssammlungen",
    searchResults: "Suchergebnisse für",
    refineSearch: "Suche verfeinern",
    filterCriteria: "Filterkriterien",
    close: "Schließen"
  }
};
const POPULAR_SUGGESTIONS = ["Silk Dress", "Linen Blazer", "High-waisted Trouser", "Cashmere Knit", "Tailored Coat"];
const CURATED_CATEGORIES = [{
  name: "Dresses",
  path: "/ShopPage",
  count: "24 styles"
}, {
  name: "Outerwear",
  path: "/ShopPage",
  count: "16 styles"
}, {
  name: "Sets & Tailoring",
  path: "/ShopPage",
  count: "12 styles"
}, {
  name: "Tops & Silk",
  path: "/ShopPage",
  count: "19 styles"
}];
const FALLBACK_TRENDING = [{
  title: "Belted Silk Crepe Midi Dress",
  category: "Dresses",
  price: "£285",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_silk_dress.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_silk_dress.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_silk_dress.png-wvc-sizes"
}, {
  title: "Double-Breasted Wool Coat",
  category: "Outerwear",
  price: "£420",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_wool_coat.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_wool_coat.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_wool_coat.webp-wvc-sizes"
}, {
  title: "High-Waist Pleated Trouser",
  category: "Bottoms",
  price: "£195",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_pleated_trouser.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_pleated_trouser.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_pleated_trouser.webp-wvc-sizes"
}, {
  title: "Structured Linen Blazer",
  category: "Sets & Tailoring",
  price: "£310",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_linen_blazer.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_linen_blazer.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_linen_blazer.webp-wvc-sizes"
}];
function ProductGridCard({
  product,
  index,
  language
}: {
  product: EcommerceProduct;
  index: number;
  language: Language;
}) {
  const t = translations[language];
  return <article data-index={index} className="group flex flex-col bg-background transition-colors duration-200">
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
        {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" /> : <div className="flex h-full w-full items-center justify-center text-muted-foreground text-xs uppercase tracking-widest">
            yishengfushi
          </div>}

        {product.on_sale && <div className="absolute top-3 left-3">
            <span className="bg-primary px-2 py-0.5 text-[10px] font-medium tracking-widest text-primary-foreground uppercase">
              {t.sale}
            </span>
          </div>}

        <div className="absolute inset-x-0 bottom-0 p-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100 hidden sm:block">
          <AddToCartButton product={product} productId={product.id} variant="default" size="sm" text={t.addBag} buttonClassName="w-full bg-primary text-primary-foreground text-xs uppercase tracking-wider py-2" showQuantity={false} />
        </div>
      </div>

      <div className="flex flex-1 flex-col pt-3 pb-2">
        {product.categories && product.categories.length > 0 && <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {product.categories[0].name}
          </span>}

        <Link to={product.link} className="mt-1 text-sm font-medium text-foreground transition-colors hover:text-primary line-clamp-1">
          {product.title}
        </Link>

        <div className="mt-1">
          <ProductPrice product={product} className="text-sm font-medium text-foreground" />
        </div>

        <div className="mt-3 sm:hidden">
          <AddToCartButton product={product} productId={product.id} variant="default" size="sm" text={t.addBag} buttonClassName="w-full bg-primary text-primary-foreground text-xs uppercase tracking-wider py-2" showQuantity={false} />
        </div>
      </div>
    </article>;
}
function SearchResultsContent({
  activeSearchQuery,
  onSelectSuggestion,
  language
}: {
  activeSearchQuery: string;
  onSelectSuggestion: (term: string) => void;
  language: Language;
}) {
  const t = translations[language];
  const {
    products,
    loading,
    error,
    refetch,
    total,
    hasNext,
    loadMore,
    isRefetching
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
        {Array.from({
        length: 8
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col space-y-3">
            <div className="aspect-[3/4] w-full animate-pulse bg-muted" />
            <div className="h-4 w-2/3 animate-pulse bg-muted" />
            <div className="h-4 w-1/3 animate-pulse bg-muted" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Unable to display catalogue results at this moment.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4 text-xs uppercase tracking-wider">
          Retry Search
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="space-y-12">
        <div className="border border-border bg-card p-10 text-center md:p-14">
          <h2 className="font-serif text-2xl font-normal text-foreground md:text-3xl">
            {t.noResults}
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {activeSearchQuery ? `${t.noMatches} "${activeSearchQuery}". ${t.exploreEdits}` : t.refineTerms}
          </p>

          <div className="mt-8">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">
              {t.suggestedTerms}
            </span>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {POPULAR_SUGGESTIONS.map((term, i) => <Button key={term} data-index={i} variant="outline" size="sm" onClick={() => onSelectSuggestion(term)} className="border-border text-xs text-foreground hover:border-foreground">
                  {term}
                </Button>)}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border-b border-border pb-3">
            <h3 className="font-serif text-xl font-normal text-foreground">
              {t.curatedCategories}
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
            {CURATED_CATEGORIES.map((cat, i) => <Link key={cat.name} to={cat.path} data-index={i} className="group flex flex-col border border-border bg-card p-4 transition-colors hover:border-foreground">
                <span className="text-sm font-medium text-foreground group-hover:text-primary">
                  {cat.name}
                </span>
                <span className="mt-1 text-xs text-muted-foreground">
                  {cat.count}
                </span>
              </Link>)}
          </div>
        </div>

        <div className="space-y-6 pt-4">
          <div className="flex items-baseline justify-between border-b border-border pb-3">
            <h3 className="font-serif text-xl font-normal text-foreground">
              {t.trendingThisSeason}
            </h3>
            <Link to="/ShopPage" className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              {t.viewFullCollection} <ArrowRight className="size-3" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-6">
            {FALLBACK_TRENDING.map((item, i) => <div key={item.title} data-index={i} className="group flex flex-col">
                <div className="aspect-[3/4] w-full overflow-hidden bg-muted">
                  <img src={item.img} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" />
                </div>
                <div className="pt-3">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    {item.category}
                  </span>
                  <p className="mt-1 text-sm font-medium text-foreground line-clamp-1">
                    {item.title}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {item.price}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-8">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
        {products.map((product, i) => <ProductGridCard key={product.id} product={product} index={i} language={language} />)}
      </div>

      {hasNext && <div className="pt-6 text-center">
          <Button variant="outline" size="lg" onClick={async () => await loadMore()} disabled={isRefetching} className="border-border px-8 text-xs uppercase tracking-widest hover:border-foreground">
            {isRefetching ? t.loading : t.loadMore}
          </Button>
        </div>}
    </div>;
}
export default function CatalogSearch() {
  const language = useWvcLanguage();
  const t = translations[language];
  const [searchValue, setSearchValue] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const activeWPQuery = useMemo(() => {
    const term = submittedQuery.trim();
    if (term) {
      return new WP_Query({
        s: term,
        post_type: "product"
      });
    }
    if (typeof window !== "undefined" && typeof (window as any).wvcClient !== "undefined" && typeof (window as any).wvcClient.getCurrentWPQueryParams === "function") {
      const currentParams = (window as any).wvcClient.getCurrentWPQueryParams();
      return new WP_Query(currentParams);
    }
    return new WP_Query({
      post_type: "product",
      per_page: 12
    });
  }, [submittedQuery]);
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedQuery(searchValue.trim());
  };
  const handleSuggestionClick = (term: string) => {
    setSearchValue(term);
    setSubmittedQuery(term);
  };
  const handleClear = () => {
    setSearchValue("");
    setSubmittedQuery("");
  };
  return <section id="搜索框与结果" data-nav="light" className="bg-background text-foreground py-10 md:py-16">
      <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-16">
        {/* Top Header & Search Area */}
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">
            {t.catalogueSearch}
          </span>
          <h1 className="mt-2 font-serif text-3xl font-normal tracking-tight text-foreground md:text-5xl">
            {t.findSignature}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {t.searchDescription}
          </p>

          <form onSubmit={handleSearchSubmit} className="relative mt-8 flex items-center shadow-xs">
            <div className="pointer-events-none absolute left-4 text-muted-foreground">
              <Search className="size-5" />
            </div>
            <Input type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)} placeholder={t.searchPlaceholder} className="h-12 w-full border-border bg-background pl-12 pr-28 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-foreground focus-visible:ring-0 rounded-none" />
            {searchValue && <Button type="button" variant="ghost" onClick={handleClear} className="absolute right-24 p-1 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent" aria-label="Clear search">
                <X className="size-4" />
              </Button>}
            <Button type="submit" variant="default" size="default" className="absolute right-1 top-1 bottom-1 h-auto bg-secondary px-5 text-xs uppercase tracking-wider text-secondary-foreground hover:bg-secondary/90 rounded-none">
              {t.search}
            </Button>
          </form>

          {/* Quick suggestions pills */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-muted-foreground">{t.popular}</span>
            {POPULAR_SUGGESTIONS.map((term, i) => <Button key={term} data-index={i} type="button" variant="link" onClick={() => handleSuggestionClick(term)} className="h-auto p-0 text-xs text-foreground/80 underline-offset-4 hover:underline hover:text-primary transition-colors cursor-pointer">
                {term}
                {i < POPULAR_SUGGESTIONS.length - 1 ? "," : ""}
              </Button>)}
          </div>
        </div>

        {/* Results Container with EcommerceProductsProvider */}
        <div className="mt-12 md:mt-16 border-t border-border pt-8">
          <EcommerceProductsProvider wp_query={activeWPQuery}>
            <div className="flex flex-col lg:flex-row lg:gap-8">
              {/* Desktop Filter Sidebar */}
              <aside className="hidden w-1/4 shrink-0 lg:block">
                <div className="sticky top-24 border-r border-border pr-6">
                  <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
                    <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      {t.refineSearch}
                    </span>
                  </div>
                  <ProductsFilter />
                </div>
              </aside>

              {/* Main Results Column */}
              <div className="w-full lg:w-3/4 space-y-6">
                {/* Result metadata & controls */}
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
                  <div>
                    <h2 className="text-sm font-medium text-foreground">
                      {submittedQuery ? <>
                          {t.searchResults}{" "}
                          <span className="italic font-serif text-base">
                            "{submittedQuery}"
                          </span>
                        </> : t.allCollections}
                    </h2>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Mobile filter trigger */}
                    <div className="lg:hidden">
                      <Button variant="outline" size="sm" onClick={() => setMobileFilterOpen(!mobileFilterOpen)} className="flex items-center gap-1.5 text-xs uppercase tracking-wider border-border">
                        <SlidersHorizontal className="size-3.5" />
                        Filters
                      </Button>
                    </div>

                    <div className="w-44">
                      <ProductSortDropdown label="Sort" />
                    </div>
                  </div>
                </div>

                {/* Mobile Filter Drawer / Expansion */}
                {mobileFilterOpen && <div className="border border-border bg-card p-4 lg:hidden">
                    <div className="mb-3 flex items-center justify-between border-b border-border pb-2">
                      <span className="text-xs font-semibold uppercase tracking-wider">
                        {t.filterCriteria}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => setMobileFilterOpen(false)} className="h-6 p-1 text-xs">
                        {t.close}
                      </Button>
                    </div>
                    <ProductsFilter />
                  </div>}

                {/* Dynamic Product Grid Consumer */}
                <SearchResultsContent activeSearchQuery={submittedQuery} onSelectSuggestion={handleSuggestionClick} language={language} />
              </div>
            </div>
          </EcommerceProductsProvider>
        </div>
      </div>
    </section>;
}